import type { LazyQueryExecFunction } from "@apollo/client/react";
import { decrypt, identity } from "deso-protocol";
import { cloneDeep } from "es-toolkit";
import type { Dispatch, SetStateAction } from "react";
import { useCallback } from "react";

import { getMediaFeedFilters } from "@/components/app/get-feed-filters";
import type { MediaPostCategoryFilter } from "@/contexts/feed-context";
import type {
  Account,
  Exact,
  GetAccessGroupMemberEncryptedPrivateKeyQuery,
  PageInfo,
  Post,
  PostsQuery,
} from "@/graphql/codegen/graphql";
import { DEFAULT_MESSAGING_GROUP_KEY_NAME } from "@/utils/constants";
import { isAbortError } from "@/utils/helpers";
import type { PostGatedContentSchema } from "@/utils/post-utils";
import { parsePostGatedContent } from "@/utils/post-utils";

import type { GetPostsDTO } from "./use-get-posts";

type UseMediaPostsProps = {
  mediaPostCategoryFilter: Record<MediaPostCategoryFilter, boolean>;
  getPosts: (params: GetPostsDTO) => Promise<PostsQuery | undefined>;
  userPublicKey?: string;
  setPostsInfo: Dispatch<SetStateAction<PageInfo | null>>;
};

export type PostWithGatedContent = Post & {
  subscriptionEncryptedImageURLs: string[];
  subscriptionEncryptedVideoURLs: string[];
  gatedContent: PostGatedContentSchema | null;
  viewerIsSubscribed: boolean;
};

type EncryptedPrivateKeyType = LazyQueryExecFunction<
  GetAccessGroupMemberEncryptedPrivateKeyQuery,
  Exact<{
    accessGroupOwnerPublicKey: string;
    accessGroupMemberPublicKey: string;
    accessGroupKeyName: string;
    accessGroupMemberKeyName: string;
  }>
>;

export const useMediaPosts = ({
  mediaPostCategoryFilter,
  getPosts,
  userPublicKey,
  setPostsInfo,
}: UseMediaPostsProps) => {
  const getGatedContent = async (
    user: Account | null,
    getEncryptedPrivateKey: EncryptedPrivateKeyType,
    postExtraData: Post["extraData"],
  ) => {
    const gatedContent = parsePostGatedContent(postExtraData);

    const { currentUser } = await identity.snapshot();
    if (
      !(
        gatedContent &&
        user?.publicKey &&
        user.publicKey === currentUser?.publicKey
      )
    ) {
      return {
        viewerIsSubscribed: false,
        gatedContent,
      };
    }

    if (!gatedContent) {
      throw new Error("No gated content to decrypt");
    }

    try {
      const request = getEncryptedPrivateKey({
        variables: {
          accessGroupOwnerPublicKey:
            gatedContent.SubscriptionAccessGroupOwnerPublicKey,
          accessGroupKeyName: gatedContent.SubscriptionAccessGroupKeyName,
          accessGroupMemberPublicKey: user.publicKey,
          accessGroupMemberKeyName: DEFAULT_MESSAGING_GROUP_KEY_NAME,
        },
      });
      request.retain?.();
      const { data } = await request;

      const encryptedSeedHexWithoutHexPrefix =
        data?.accessGroupMember?.encryptedKey?.slice(2);

      if (!encryptedSeedHexWithoutHexPrefix) {
        return {
          viewerIsSubscribed: false,
          gatedContent,
        };
      }

      const { seedHex } = await identity.decryptAccessGroupKeyPair(
        encryptedSeedHexWithoutHexPrefix,
      );

      const imageUrls =
        gatedContent.SubscriptionEncryptedImageURLs.length > 0
          ? JSON.parse(
              await decrypt(
                seedHex,
                gatedContent.SubscriptionEncryptedImageURLs,
              ),
            )
          : [];

      const videoUrls =
        gatedContent.SubscriptionEncryptedVideoURLs.length > 0
          ? JSON.parse(
              await decrypt(
                seedHex,
                gatedContent.SubscriptionEncryptedVideoURLs,
              ),
            )
          : [];

      return { imageUrls, videoUrls, gatedContent, viewerIsSubscribed: true };
    } catch (e) {
      if (isAbortError(e)) {
        return {
          viewerIsSubscribed: false,
          gatedContent,
        };
      }

      throw new Error(`Error decrypting gated content: ${e}`);
    }
  };

  const fetchMediaPosts = useCallback(
    async (
      user: Account | null,
      getEncryptedPrivateKey: EncryptedPrivateKeyType,
      isInitial?: boolean,
      hasNextPage = true,
      offset = 0,
    ) => {
      let newMediaPosts: Post[] = [];
      const hasAllowed = isInitial || hasNextPage;
      let totalMediaCount = 0;

      if (hasAllowed) {
        while (totalMediaCount < 20) {
          const filters = getMediaFeedFilters({
            userPublicKey,
            mediaPostCategoryFilter,
          });

          const data = await getPosts({ filters, offset });
          const nodes = data?.posts?.nodes ?? [];
          const pageInfo = data?.posts?.pageInfo as PageInfo;

          const cloneNodes = cloneDeep(
            nodes.filter(Boolean),
          ) as PostWithGatedContent[];

          for (const post of cloneNodes) {
            if (
              post.extraData?.SubscriptionEncryptedImageURLs ||
              post.extraData?.SubscriptionEncryptedVideoURLs
            ) {
              try {
                const gatedData = await getGatedContent(
                  user,
                  getEncryptedPrivateKey,
                  post.extraData,
                );

                if (gatedData.viewerIsSubscribed) {
                  post.subscriptionEncryptedImageURLs = gatedData.imageUrls;
                  post.subscriptionEncryptedVideoURLs = gatedData.videoUrls;
                } else {
                  post.viewerIsSubscribed = gatedData.viewerIsSubscribed;
                  post.gatedContent = gatedData.gatedContent;
                }
              } catch (e) {
                console.error("Error fetching gated content", e);
              }
            }

            totalMediaCount +=
              (post.imageUrls?.length ?? 0) +
              (post.videoUrls?.length ?? 0) +
              (post.extraData?.SubscriptionEncryptedImageURLs?.length || 0) +
              (post.extraData?.SubscriptionEncryptedVideoURLs?.length || 0);
          }
          newMediaPosts = [
            ...newMediaPosts,
            ...cloneNodes.filter(
              (post) =>
                post.imageUrls?.length ||
                post.videoUrls?.length ||
                post.extraData?.SubscriptionEncryptedImageURLs ||
                post.extraData?.SubscriptionEncryptedVideoURLs,
            ),
          ];

          setPostsInfo(pageInfo);

          hasNextPage = pageInfo.hasNextPage;

          if (!hasNextPage) {
            break;
          }
        }

        return newMediaPosts || [];
      }
    },
    [getPosts, mediaPostCategoryFilter, setPostsInfo, userPublicKey],
  );

  return fetchMediaPosts;
};
